import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";
import WasmController from "react-lib/frameworks/WasmController";

/** ======================= APIs =======================  */
import ClockRecordList from "issara-sdk/apis/ClockRecordList_apps_HRM";
import ClockRecordHistoryList from "issara-sdk/apis/ClockRecordHistoryList_apps_HRM";
import ClockRecordDetail from "issara-sdk/apis/ClockRecordDetail_apps_HRM";

/** =================== Serializers ====================  */
import ClockRecordSerializer from "issara-sdk/types/ClockRecordSerializer_apps_HRM";
import ClockRecordHistorySerializer from "issara-sdk/types/ClockRecordHistorySerializer_apps_HRM";
import { beStringToAdString } from "react-lib/utils/dateUtils";
import { formatTimeHideSeconds } from "../HRMInterface";


/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  SEARCH: "SEARCH",
  HISTORY: "HISTORY",
  ADD: "ADD",
  EDIT: "EDIT",
} as const;

export const SEQUENCE_NAME = "EmployeeTA" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeeTASequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: FilterType;
    addManual: ClockRecordType;
    selectDetail: ClockRecordType;
    historyList: ClockRecordHistorySerializer[];
    pagination: { activePage: number; items: ClockRecordSerializer[]; total: number };
  }> | null;
}>;

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "errorMessage"
  | "searchedItemListWithKey"
  | "selectedDivision"
  | "successMessage"
>;

export type ClockRecordType = Partial<ClockRecordSerializer & { date?: string, time?: string }>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type FilterType = Partial<{
  affiliate: number | null;
  employee: number | null;
  clock_end_date: string; // *
  type: string;
  clock_start_date: string;
}>;

type ClockRecordListActionParams = {
  method: "list" | "create";
  controller: WasmController<State & PickedState, Event, Data, {}>;
  data?: ClockRecordType;
  params?: any;
};

type ClockRecordDetailActionParams = {
  method: "retrieve" | "update";
  controller: WasmController<State & PickedState, Event, Data, {}>;
  pk: number;
  data?: ClockRecordType;
  params?: any;
};

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  userProfile?: any;
};

export type SeqState = BaseSeqState<SequenceName> & { isSelf?: boolean };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { activePage?: number; isSelf?: boolean },
  [ACTIONS.HISTORY]: { clockId: number },
  [ACTIONS.ADD]: {},
  [ACTIONS.EDIT]: { clockId: number; item?: ClockRecordType },
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeTASequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const;

export const LIST_LIMIT = 40;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      EmployeeTASequence: {
        ...state.EmployeeTASequence,
        sequenceIndex: "Action",
        filter: { affiliate: state.selectedDivision?.id },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.HISTORY]: HandleHistory,
    [ACTIONS.ADD]: HandleAddManually,
    [ACTIONS.EDIT]: HandleEdit,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const { isSelf } = params;

  const filter = state.EmployeeTASequence?.filter;
  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * LIST_LIMIT;

  const formattedFilter: FilterType = {
    ...filter,
    clock_start_date: beStringToAdString(filter?.clock_start_date || ""),
    clock_end_date: beStringToAdString(filter?.clock_end_date || ""),
    employee: isSelf ? controller.data.userProfile?.employee : filter?.employee,
  };

  // console.log(filter, offset);

  const [listResp] = await GetClockRecordList(controller, {
    filter: formattedFilter,
    limit: LIST_LIMIT,
    offset,
  });

  controller.setState({
    EmployeeTASequence: {
      ...state.EmployeeTASequence,
      pagination: {
        activePage,
        items: listResp?.items || [],
        total: listResp?.total || 0,
      },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleHistory: Handler<Params<Actions["HISTORY"]>> = async (controller, params) => {
  const state = controller.getState();

  const { clockId } = params;

  const [histResp, histErr] = await GetClockHistoryList(controller, { clockId });

  controller.setState({
    EmployeeTASequence: {
      ...state.EmployeeTASequence,
      historyList: histResp?.items || [],
    },
  });
};

const HandleAddManually: Handler<Params<Actions["ADD"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD]: BLC_STATUS.LOADING },
  });

  const addManual = state.EmployeeTASequence?.addManual;
  const addManualTime = state.EmployeeTASequence?.addManual?.time;
  const formattedDate = beStringToAdString(state.EmployeeTASequence?.addManual?.date || "");
  const formattedDateTime = [formattedDate, addManualTime].filter(str => str).join(" ");

  const formattedData: ClockRecordType = {
    ...addManual,
    clock_datetime: formattedDateTime,
  };

  const pagination = state.EmployeeTASequence?.pagination;

  const [addResp, addErr] = await AddClockRecordManually(controller, { data: formattedData });

  if (addErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.ADD]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: addErr,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.ADD]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [SEQUENCE_NAME]: "Add Clock Record Success",
    },
  });

  HandleSearch(controller, { ...params, action: "SEARCH", activePage: pagination?.activePage });
};

const HandleEdit: Handler<Params<Actions["EDIT"]>> = async (controller, params) => {
  const state = controller.getState();

  const { clockId, item } = params;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.EDIT]: BLC_STATUS.LOADING },
  });

  const editData = state.EmployeeTASequence?.selectDetail;
  const selectedTime = formatTimeHideSeconds(item?.time);
  const editDataTime = state.EmployeeTASequence?.selectDetail?.time ?? selectedTime;
  const formattedDate = beStringToAdString(item?.date || "");
  const formattedDateTime = [formattedDate, editDataTime].filter(str => str).join(" ");

  const formattedData: ClockRecordType = {
    ...editData,
    clock_datetime: formattedDateTime,
    status: editData?.status ?? "",
  };

  const pagination = state.EmployeeTASequence?.pagination;

  const [editResp, editErr] = await EditClockRecord(controller, { clockId, data: formattedData });

  if (editErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.EDIT]: BLC_STATUS.ERROR
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: editErr,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EDIT]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [SEQUENCE_NAME]: "Edit Clock Time Success",
    },
  });

  HandleSearch(controller, { ...params, action: "SEARCH", activePage: pagination?.activePage });
};

/** ====================================================  */
/**                         API                           */
/** ====================================================  */

const GetClockRecordList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { filter, limit, offset } = params as any;

  return await HandleClockRecordListApi(controller, {
    method: "list",
    apiParams: { ...filter, limit, offset },
  });
};

const GetClockHistoryList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { clockId } = params;

  const [resp, err] = await ClockRecordHistoryList.list({
    apiToken: controller.apiToken,
    params: {
      id: clockId,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const AddClockRecordManually: Handler = async (controller, params) => {
  const state = controller.getState();

  const { data } = params;

  return await HandleClockRecordListApi(controller, {
    method: "create",
    data,
  });
};

const GetClockRecordDetail: Handler = async (controller, params) => {
  const state = controller.getState();

  const { clockId } = params;

  return await HandleClockRecordDetailApi(controller, {
    method: "retrieve",
    pk: clockId,
  });
};

const EditClockRecord: Handler = async (controller, params) => {
  const state = controller.getState();

  const { clockId, data } = params;

  return await HandleClockRecordDetailApi(controller, {
    method: "update",
    pk: clockId,
    data,
  });
};

/** ====================================================  */
/**                     API HANDLER                       */
/** ====================================================  */

const HandleClockRecordListApi: Handler<{
  method: "list" | "create";
  data?: ClockRecordType;
  apiParams?: any;
}> = async (controller, params) => {
  const commonParams = {
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  };

  const { method, data, apiParams } = params;

  const actionMethod = ClockRecordList[method];

  if (!actionMethod) return [null, new Error(`Invalid method: ${method}`)];

  let callParams: any = commonParams;

  if (method === "list") {
    callParams = { ...commonParams, params: apiParams };
  } else if (method === "create") {
    callParams = { ...callParams, data };
  }

  const [resp, err] = await actionMethod(callParams);

  if (err) return [null, err];

  return [resp, null];
};

const HandleClockRecordDetailApi: Handler<{
  method: "retrieve" | "update";
  pk: number;
  data?: ClockRecordType;
  apiParams?: any;
}> = async (controller, params) => {

  const { method, pk, data, apiParams } = params;

  const commonParams = {
    apiToken: controller.apiToken,
    pk,
    extra: {
      division: controller.data.division,
    },
  };

  const actionMethod = ClockRecordDetail[method];

  if (!actionMethod) return [null, new Error(`Invalid method: ${method}`)];

  let callParams: any = commonParams;

  if (method === "retrieve") {
    callParams = { ...commonParams, params: apiParams };
  } else if (method === "update") {
    callParams = { ...callParams, data };
  }

  const [resp, err] = await actionMethod(callParams);

  if (err) return [null, err];

  return [resp, null];
};
